import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_ROUTES } from 'src/app/@core/auth/constants/auth-constants';
import { ENROLL_EVENT_REPORT } from 'src/app/@constants/local-storage-key-constants';
import { EventEnrollUser } from 'src/app/@data-services/event-enroll/event-enroll.data-model';
import { BackendService } from 'src/app/@networking/core/backend.service';
import { SessionService } from 'src/app/@networking/core/session.service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from 'src/app/@utils/local-storage-util';
import { resolveMatchmakingEventIdFromState } from 'src/app/@utils/matchmaking/resolve-matchmaking-event-id';

/**
 * Bridges a validated attendee email into the show-winners networking session.
 * Shared by HomeComponent (email dialog) and the Cognito OTP login flow.
 */
@Injectable({ providedIn: 'root' })
export class NetworkingSessionService {
  private readonly _backend = inject(BackendService);
  private readonly _session = inject(SessionService);
  private readonly _router = inject(Router);
  private readonly _eventConfigState = inject(EventConfigStateService);

  /**
   * Seed show-winners x-user-id / x-user-email headers from a signed-in email.
   * Idempotent — safe to call on Amplify login, enrollment, and app boot.
   */
  syncShowWinnersIdentity(email: string, eventId?: string): void {
    const id = email.toLowerCase().trim();
    if (!id) return;

    const expoId = resolveMatchmakingEventIdFromState(
      this._eventConfigState,
      undefined,
      eventId
    );
    const current = this._session.session();
    this._session.set({
      userId: id,
      role: 'attendee',
      expoId,
      name: id.split('@')[0],
      enriched: current?.enriched ?? false,
      mode: current?.mode ?? 'event',
    });

    const enroll = getLocalStorageItem<EventEnrollUser>(ENROLL_EVENT_REPORT);
    if (!enroll?.email || enroll.email.toLowerCase().trim() !== id) {
      setLocalStorageItem<EventEnrollUser>(ENROLL_EVENT_REPORT, {
        eventNameIdentifier: eventId || enroll?.eventNameIdentifier || expoId,
        sendEventReportNotification:
          enroll?.sendEventReportNotification ?? false,
        email: id,
      });
    }
  }

  /** Establish the show-winners session from the signed-in email. */
  async establishNetworkingSession(
    email: string,
    opts: { navigate?: boolean; eventId?: string } = {}
  ): Promise<void> {
    const navigate = opts.navigate ?? true;
    const id = email.toLowerCase().trim();
    if (!id) return;

    const expoId = resolveMatchmakingEventIdFromState(
      this._eventConfigState,
      undefined,
      opts.eventId
    );
    this._backend.resetForNewSession();
    this.syncShowWinnersIdentity(id, opts.eventId);

    try {
      const exists = await this._backend.attendeeExists(id);
      const enriched =
        exists === false ? false : await this._backend.attendeeEnriched(id);
      this._session.set({
        userId: id,
        role: 'attendee',
        expoId,
        name: id.split('@')[0],
        enriched,
        mode: 'event',
      });
    } catch {
      /* transient backend error — keep the header session */
    }

    if (navigate) {
      void this._router.navigate([AUTH_ROUTES.HOME]);
    }
  }
}
